/**
 * 页面层通用构件。
 *
 * 为什么不放进 `src/components/`：那批组件是跨页面契约，由别人维护；
 * 这里的东西只服务于 `src/pages/` 的版式，属于「页面之间的共用」，不是全站契约。
 * 一旦某个构件被画布或布局也用上，再提升到 components 才有意义。
 *
 * 颜色一律来自 pages.css 的类，本文件不出现任何字面色值。
 */

import type { ReactNode } from 'react';
import type { NodeStatus, RunStatus } from '@/types/api';
import { StatusTag } from '@/components';

import './pages.css';

// ---------------------------------------------------------------- 状态胶囊
//
// 配色/图标/文案由 components/StatusTag 统一负责（它已经走 --status-* 三件套，
// 并且给 SKIPPED 上了灰化 + 虚线 + 减号三重区分）。这里只补一层悬停解释：
// 状态名再准确也只有两三个字，「已跳过到底算不算出错」需要一句话说清，
// 而这句话属于页面文案，不属于全站标签契约。

const RUN_HINT: Record<RunStatus, string> = {
  QUEUED: '已入队，等待 Worker 领取',
  RUNNING: '正在执行，节点状态会实时推送',
  SUCCESS: '全部节点执行完成',
  FAILED: '有节点执行出错，可在节点明细里定位',
  CANCELLING: '已发出取消指令，等待 Worker 在下一轮调度感知',
  CANCELLED: '被人为终止，未跑完的节点不会再执行',
};

const NODE_HINT: Record<NodeStatus, string> = {
  PENDING: '上游还没就绪',
  RUNNING: '正在执行',
  SUCCESS: '执行成功',
  FAILED: '执行出错，展开这一行看错误码与入出参',
  // 全站最容易被误读的一个状态：用户看到「不是成功」就以为出了事，
  // 所以这句提示必须直接否定「出错」这个读法，而不只是复述状态名。
  SKIPPED: '条件分支没有走到这个节点。这不是错误，无需排查',
  TIMEOUT: '超过节点超时上限被中止',
  CANCELLED: '整个 Run 被取消，该节点未跑完',
};

export function RunStatusPill({ status }: { status: RunStatus }) {
  return <StatusTag status={status} tooltip={RUN_HINT[status]} />;
}

export function NodeStatusPill({ status }: { status: NodeStatus }) {
  return <StatusTag kind="node" status={status} tooltip={NODE_HINT[status]} />;
}

// ---------------------------------------------------------------- 语义胶囊

type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export function TonePill({
  tone = 'neutral',
  icon,
  children,
}: {
  tone?: Tone;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={`pg-pill pg-pill--tone-${tone}`}>
      {icon}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- 管理页摘要

export interface ManagementHeroStat {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
}

export function ManagementHero({
  eyebrow,
  title,
  description,
  icon,
  tone = 'accent',
  status,
  summaryLabel = '当前状态',
  stats,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  description: ReactNode;
  icon: ReactNode;
  tone?: Tone;
  status?: ReactNode;
  summaryLabel?: ReactNode;
  stats: ManagementHeroStat[];
}) {
  return (
    <section className={`pg-admin-hero pg-admin-hero--${tone}`}>
      <div className="pg-admin-hero__content">
        <span className="pg-admin-hero__eyebrow">
          {icon}
          {eyebrow}
        </span>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <div className="pg-admin-hero__summary">
        <div className="pg-admin-hero__summary-head">
          <span>{summaryLabel}</span>
          {status}
        </div>
        <div className="pg-admin-hero__stats">
          {stats.map((stat, index) => (
            <div className="pg-admin-hero__stat" key={index}>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              {stat.note ? <small>{stat.note}</small> : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PanelHeading({
  icon,
  title,
  description,
  aside,
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="pg-panel-heading">
      <span className="pg-panel-heading__icon">{icon}</span>
      <div className="pg-panel-heading__copy">
        <span className="pg-panel-heading__title">{title}</span>
        {description ? <span className="pg-panel-heading__desc">{description}</span> : null}
      </div>
      {aside ? <span className="pg-panel-heading__aside">{aside}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- 面板

export function Panel({
  title,
  extra,
  flush,
  children,
}: {
  title?: ReactNode;
  extra?: ReactNode;
  /** 内容自带内边距（如整块是表格）时关掉 body 的 padding */
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="pg-panel">
      {title ? (
        <header className="pg-panel__head">
          <div className="pg-panel__title">{title}</div>
          {extra ? <div className="pg-row">{extra}</div> : null}
        </header>
      ) : null}
      <div className={flush ? 'pg-panel__body pg-panel__body--flush' : 'pg-panel__body'}>
        {children}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- 指标

export function MetricGrid({ children }: { children: ReactNode }) {
  return <div className="pg-metrics">{children}</div>;
}

export function Metric({
  icon,
  label,
  value,
  note,
  tone,
}: {
  icon?: ReactNode;
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  /** 超阈值时整块换底，而不只是把数字染色——一屏十几个数字时字色会被漏掉 */
  tone?: 'accent' | 'success' | 'warning' | 'danger';
}) {
  return (
    <div className={tone ? `pg-metric pg-metric--${tone}` : 'pg-metric'}>
      <div className="pg-metric__top">
        {icon ? <span className="pg-metric__icon">{icon}</span> : null}
        <span className="pg-metric__label">{label}</span>
      </div>
      <span className="pg-metric__value">{value}</span>
      {note ? <span className="pg-metric__note">{note}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- 通告条

export function Banner({
  tone,
  icon,
  title,
  description,
  action,
}: {
  tone: 'danger' | 'warning' | 'info' | 'neutral';
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`pg-banner pg-banner--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      {icon ? <span className="pg-banner__icon">{icon}</span> : null}
      <div className="pg-banner__body">
        <div className="pg-banner__title">{title}</div>
        {description ? <div className="pg-banner__desc">{description}</div> : null}
      </div>
      {action ? <div className="pg-row">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- 空状态

export function EmptyState({
  icon,
  title,
  description,
  action,
  muted,
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  /** 「筛选没命中」用中性色；「这里本来就还没有东西」用强调色引导创建 */
  muted?: boolean;
}) {
  return (
    <div className="pg-empty">
      <div className={muted ? 'pg-empty__icon pg-empty__icon--muted' : 'pg-empty__icon'}>
        {icon}
      </div>
      <div className="pg-empty__title">{title}</div>
      {description ? <div className="pg-empty__desc">{description}</div> : null}
      {action ? <div className="pg-empty__action">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- 定义列表

export function DefList({ children }: { children: ReactNode }) {
  return <div className="pg-defs">{children}</div>;
}

export function Def({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="pg-def">
      <span className="pg-def__label">{label}</span>
      <span className="pg-def__value">{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------- 筛选片

export function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="pg-chip" aria-pressed={active} onClick={onClick}>
      {children}
    </button>
  );
}
